import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem, IconButton, Typogaphy } from 'vnetwork-ui'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import PersonIcons from '@/icons/PersonIcons'
import FieldItem from '../shared/FieldItem'
import { Cv } from '../../../../types'
import EditIcons from '@/icons/EditIcons'

interface CandidateInformationProps {
    cv: any
    updateCVMutation: (values: Cv) => Promise<any>
}

const CandidateInformation: React.FC<CandidateInformationProps> = ({
    cv,
    updateCVMutation,
}) => {
    const { t } = useTranslation()
    const { t: tResume } = useTranslation('resume')
    const [open, setOpen] = useState(false)
    const form = useForm({
        defaultValues: {
            name: cv?.result?.name || '',
            email: cv?.result?.email || '',
            phone: cv?.result?.phone || '',
            backup_phone: cv?.result?.backup_phone || '',
            address: cv?.result?.address || '',
            day_of_birth: cv?.result?.day_of_birth
                ? dayjs(cv?.result?.day_of_birth).toDate()
                : null,
            about: cv?.result?.about || '',
        },
    })

    const handleSubmit = async (values: any) => {
        await updateCVMutation({
            ...(cv?.result as Cv),
            ...values,
            day_of_birth: values.day_of_birth
                ? dayjs(values.day_of_birth).format('YYYY-MM-DD')
                : null,
        } as Cv)
        setOpen(false)
    }

    return (
        <div className="bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-3 py-2 border-b border-gray-200">
                <div className="flex items-center gap-2 justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                            <PersonIcons className="w-4 h-4 text-white" />
                        </div>
                        <Typogaphy className="font-medium text-base text-gray-800">
                            {tResume('candidate-information')}
                        </Typogaphy>
                    </div>
                    <IconButton
                        size="small"
                        color="primary"
                        className="bg-gradient-to-br from-blue-500 to-indigo-600"
                        onClick={() => setOpen(true)}
                    >
                        <EditIcons className="w-4 h-4 text-white" />
                    </IconButton>
                </div>
            </div>
            <div className="p-3 space-y-1">
                <FieldItem label={t('name')} value={cv?.result?.name} />
                <FieldItem label={t('email')} value={cv?.result?.email} />
                <FieldItem
                    label={t('day-of-birthday')}
                    value={
                        cv?.result?.day_of_birth
                            ? dayjs(cv?.result?.day_of_birth).format(
                                  'DD/MM/YYYY'
                              )
                            : null
                    }
                />
                <FieldItem label={t('phone')} value={cv?.result?.phone} />
                <FieldItem
                    label={tResume('backup-phone')}
                    value={cv?.result?.backup_phone}
                />
                <div className="px-2 -mt-1 mb-1">
                    <Typogaphy className="text-xs text-gray-500 italic">
                        {tResume('backup-phone-note', {
                            defaultValue:
                                'Dùng khi số chính không liên lạc được.',
                        })}
                    </Typogaphy>
                </div>
                <FieldItem label={t('address')} value={cv?.result?.address} />
                {cv?.result?.about && (
                    <div className="grid grid-cols-3 gap-2 py-1.5 px-2 hover:bg-gray-50 transition-colors">
                        <Typogaphy className="font-medium text-sm text-gray-600">
                            {t('about')}:
                        </Typogaphy>
                        <Typogaphy className="col-span-2 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                            {cv?.result?.about}
                        </Typogaphy>
                    </div>
                )}
            </div>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                title={t('edit')}
            >
                <form
                    className="space-y-3"
                    onSubmit={form.handleSubmit(handleSubmit)}
                >
                    <FormItem
                        control={form.control}
                        name="name"
                        type="textfield"
                        label={t('name')}
                    />
                    <FormItem
                        control={form.control}
                        name="email"
                        type="textfield"
                        label={t('email')}
                    />
                    <FormItem
                        control={form.control}
                        name="phone"
                        type="textfield"
                        label={t('phone')}
                    />
                    <FormItem
                        control={form.control}
                        name="backup_phone"
                        type="textfield"
                        label={tResume('backup-phone')}
                    />
                    <FormItem
                        control={form.control}
                        name="address"
                        type="textfield"
                        label={t('address')}
                    />
                    <FormItem
                        control={form.control}
                        name="day_of_birth"
                        type="datepicker"
                        label={t('day-of-birthday')}
                    />
                    <FormItem
                        control={form.control}
                        name="about"
                        type="textarea"
                        label={t('about')}
                    />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button
                            variant="outlined"
                            onClick={() => setOpen(false)}
                        >
                            {t('cancel')}
                        </Button>
                        <Button variant="contained" type="submit">
                            {t('save')}
                        </Button>
                    </div>
                </form>
            </Dialog>
        </div>
    )
}

export default CandidateInformation
